import type { ComputedRef } from 'vue'
import type { OrderDetails } from '@widgets/orders'
import type { OrderInvoiceFields, OrderInvoiceFile } from './interfaces'
import type { OrderInvoicePreviewState } from './types'

import { OrderStatus } from '@brighterly/lib-core-types/orders/enums'

import { useSidebarPrivileges } from '@entities/viewer'

import { useOrderStore } from '../store'
import { useOrderInvoiceStore } from './store'
import { useOrderInvoiceDownload } from '../../api/invoice/invoice-download.hook'
import { useOrderInvoicePreview } from '../../api/invoice/invoice-preview.hook'
import { useOrderInvoiceSave } from '../../api/invoice/invoice-save.hook'
import { useOrderInvoicesApi } from '../../api/invoice/invoices.hook'
import { downloadBlob } from '../../lib/download-blob'
import { areInvoiceFieldsEqual, cloneInvoiceFields, isInvoiceFieldsEmpty, toInvoicePayload } from '../../lib/invoice-fields'

const EMPTY_FIELDS: OrderInvoiceFields = { parent_full_name: null, address: null, items: [] }

interface OrderInvoiceOptions {
  orderId: ComputedRef<number>
}

interface OrderInvoiceReturn {
  order: ComputedRef<OrderDetails | null>
  draft: ComputedRef<OrderInvoiceFields | null>
  invoices: ComputedRef<OrderInvoiceFile[]>
  preview: ComputedRef<Blob | null>
  previewState: ComputedRef<OrderInvoicePreviewState>
  isLoading: ComputedRef<boolean>
  isPreviewLoading: ComputedRef<boolean>
  isPaid: ComputedRef<boolean>
  canManage: ComputedRef<boolean>
  canSave: ComputedRef<boolean>
  canClear: ComputedRef<boolean>
  setParentFullName: (value: string) => void
  setAddress: (value: string) => void
  setItemDetails: (orderItemId: number, value: string) => void
  clearFields: () => void
  previewInvoice: () => Promise<void>
  saveInvoice: () => Promise<void>
  downloadInvoice: (invoice: OrderInvoiceFile) => Promise<void>
}

export const useOrderInvoice = ({ orderId }: OrderInvoiceOptions): OrderInvoiceReturn => {
  const orderStore = useOrderStore()
  const store = useOrderInvoiceStore()
  const { canManageOrders } = useSidebarPrivileges()

  const order = computed<OrderDetails | null>(() => orderStore.getOrder)
  const draft = computed<OrderInvoiceFields | null>(() => store.getDraft)
  const saved = computed<OrderInvoiceFields | null>(() => store.getSaved)
  const invoices = computed<OrderInvoiceFile[]>(() => store.getInvoices)
  const preview = computed<Blob | null>(() => store.getPreview)
  const previewState = computed<OrderInvoicePreviewState>(() => store.getPreviewState)
  const isLoading = computed<boolean>(() => store.isLoading)
  const isPreviewLoading = computed<boolean>(() => store.isPreviewLoading)

  const isOrderLoaded = computed<boolean>(() => order.value?.id === orderId.value)
  const isPaid = computed<boolean>(() => order.value?.status === OrderStatus.SUCCEEDED)
  const canLoadInvoices = computed<boolean>(() => isOrderLoaded.value && isPaid.value)
  const canManage = computed<boolean>(() => canManageOrders.value)
  const isBusy = computed<boolean>(() => isLoading.value || isPreviewLoading.value)

  const isPageLeft = ref<boolean>(false)

  const isDirty = computed<boolean>(() => {
    if (!draft.value || !saved.value) return false

    return !areInvoiceFieldsEqual(draft.value, saved.value)
  })

  const canSave = computed<boolean>(() => {
    if (!canManage.value || !draft.value || isBusy.value) return false

    return !store.hasInvoices || isDirty.value
  })

  const canClear = computed<boolean>(() => {
    if (!canManage.value || !draft.value || isBusy.value) return false

    return !isInvoiceFieldsEmpty(draft.value)
  })

  const setParentFullName = (value: string): void => {
    store.setDraftParentFullName(value)
  }

  const setAddress = (value: string): void => {
    store.setDraftAddress(value)
  }

  const setItemDetails = (orderItemId: number, value: string): void => {
    store.setDraftItemDetails(orderItemId, value)
  }

  const clearFields = (): void => {
    store.clearDraft()
  }

  const showSavedFile = async (invoice: OrderInvoiceFile): Promise<void> => {
    const blob = await useOrderInvoiceDownload(orderId.value, invoice.id)
    if (!blob || isPageLeft.value) return

    store.setPreview(blob, 'saved')
  }

  const showAutomaticInvoice = async (): Promise<void> => {
    const blob = await useOrderInvoicePreview(orderId.value, toInvoicePayload(EMPTY_FIELDS))
    if (!blob || isPageLeft.value) return

    store.setPreview(blob, 'automatic')
  }

  const loadPage = async (): Promise<void> => {
    const data = await useOrderInvoicesApi(orderId.value)
    if (!data || isPageLeft.value) return

    store.setFields(data.fields)
    store.setInvoices(data.invoices)

    const latest = data.invoices[0]

    await (latest ? showSavedFile(latest) : showAutomaticInvoice())
  }

  const previewInvoice = async (): Promise<void> => {
    if (!draft.value) return

    const latest = invoices.value[0]

    if (latest && !isDirty.value) {
      await showSavedFile(latest)
      return
    }

    const blob = await useOrderInvoicePreview(orderId.value, toInvoicePayload(draft.value))
    if (!blob || isPageLeft.value) return

    store.setPreview(blob, 'unsaved')
  }

  const saveInvoice = async (): Promise<void> => {
    if (!draft.value) return

    const sent = cloneInvoiceFields(draft.value)
    const invoice = await useOrderInvoiceSave(orderId.value, toInvoicePayload(sent))
    if (!invoice || isPageLeft.value) return

    store.addInvoice(invoice)
    store.setSaved(sent)

    await showSavedFile(invoice)
  }

  const downloadInvoice = async (invoice: OrderInvoiceFile): Promise<void> => {
    const blob = await useOrderInvoiceDownload(orderId.value, invoice.id)
    if (!blob) return

    downloadBlob(blob, invoice.filename)
  }

  onScopeDispose(() => {
    isPageLeft.value = true
  })

  watch(canLoadInvoices, (canLoad) => {
    if (canLoad) void loadPage()
  }, { immediate: true })

  return {
    order,
    draft,
    invoices,
    preview,
    previewState,
    isLoading,
    isPreviewLoading,
    isPaid,
    canManage,
    canSave,
    canClear,
    setParentFullName,
    setAddress,
    setItemDetails,
    clearFields,
    previewInvoice,
    saveInvoice,
    downloadInvoice,
  }
}
